import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Settings, Building2, Users, Shield, Mail, MessageCircle, Phone,
  Landmark, Percent, Database, Save, Eye, EyeOff, Plus, X, Edit3,
  Clock, Activity, Key, CheckCircle, Bot, HelpCircle, Upload, MapPin,
  AlertTriangle, RefreshCw, Lock, Globe, Smartphone
} from 'lucide-react';
import { useAdminStore } from '../store/adminStore';
import { formatDateTime, getRelativeTime } from '../utils/helpers';
import { useAIStore } from '../../ai/aiStore';
import { useBusinessSettingsStore } from '../store/businessSettingsStore';
import { useWorkflowStore } from '../store/workflowStore';
import { toast } from '../components/Toast';

const settingsSections = [
  { id: 'business', label: 'Business Profile', icon: Building2 },
  { id: 'company', label: 'Company Info', icon: Settings },
  { id: 'users', label: 'Users & Roles', icon: Users },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'integrations', label: 'Integrations', icon: MessageCircle },
  { id: 'finance', label: 'Finance', icon: Landmark },
  { id: 'ai', label: 'AI Assistant', icon: Bot },
  { id: 'activity', label: 'Activity Logs', icon: Clock },
  { id: 'backup', label: 'Backup & Reset', icon: Database },
];

function ToggleSwitch({ value, onChange, label, description }: {
  value: boolean; onChange: (v: boolean) => void; label: string; description?: string;
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
      <div>
        <div className="text-[13px] font-medium text-gray-900">{label}</div>
        {description && <div className="text-[11px] text-gray-400 mt-0.5">{description}</div>}
      </div>
      <button
        onClick={() => onChange(!value)}
        className={`w-12 h-6 rounded-full transition-colors relative flex-shrink-0 ${value ? 'bg-[#7FA86A]' : 'bg-gray-300'}`}
      >
        <div className={`w-5 h-5 bg-white rounded-full shadow-sm absolute top-0.5 transition-transform ${value ? 'translate-x-6' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );
}

export default function SettingsPage() {
  const { activityLogs } = useAdminStore();
  const { settings: aiSettings, updateSettings: updateAISettings } = useAIStore();
  const { settings: bizSettings, updateSettings: updateBizSettings, changeAdminPassword, verifyAdminPassword } = useBusinessSettingsStore();
  const { resetBusinessData } = useWorkflowStore();
  const { resetBusinessData: resetAdminData } = useAdminStore();

  const [activeSection, setActiveSection] = useState('business');
  const [companyEmail, setCompanyEmail] = useState('info@k-tec.lk');
  const [vatNumber, setVatNumber] = useState('VAT12345678');
  const [taxRate, setTaxRate] = useState(15);
  const [whatsappApi, setWhatsappApi] = useState('');
  const [saved, setSaved] = useState(false);
  const [sessionTimeout, setSessionTimeout] = useState(30);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Business profile local state (synced to store on save)
  const [bizName, setBizName] = useState(bizSettings.businessName);
  const [bizPhone, setBizPhone] = useState(bizSettings.phone);
  const [bizWhatsapp, setBizWhatsapp] = useState(bizSettings.whatsappNumber);
  const [bizAddress, setBizAddress] = useState(bizSettings.address);
  const [bizMapsLink, setBizMapsLink] = useState(bizSettings.mapsLink);
  const [bizEmail, setBizEmail] = useState(bizSettings.email);
  const [bizWebsite, setBizWebsite] = useState(bizSettings.website);
  const [bizTagline, setBizTagline] = useState(bizSettings.tagline);
  const [bizLogo, setBizLogo] = useState<string | null>(bizSettings.logo);
  const [bizSaved, setBizSaved] = useState(false);

  // Password change state
  const [curPassword, setCurPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurPwd, setShowCurPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfPwd, setShowConfPwd] = useState(false);
  const [pwdError, setPwdError] = useState('');
  const [pwdSuccess, setPwdSuccess] = useState(false);

  // Reset data state
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [showResetWarning, setShowResetWarning] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  const handleSave = () => { setSaved(true); setTimeout(() => setSaved(false), 2000); };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Invalid file', 'Please select an image file.'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('File too large', 'Max 2MB allowed.'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setBizLogo(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleSaveBusiness = () => {
    updateBizSettings({
      businessName: bizName,
      phone: bizPhone,
      whatsappNumber: bizWhatsapp,
      address: bizAddress,
      mapsLink: bizMapsLink,
      email: bizEmail,
      website: bizWebsite,
      tagline: bizTagline,
      logo: bizLogo,
    });
    setBizSaved(true);
    toast.success('Saved', 'Business profile updated.');
    setTimeout(() => setBizSaved(false), 2500);
  };

  const handleChangePassword = () => {
    setPwdError('');
    if (!curPassword) { setPwdError('Enter current password.'); return; }
    if (!verifyAdminPassword(curPassword)) { setPwdError('Current password is incorrect.'); return; }
    if (!newPassword || newPassword.length < 6) { setPwdError('New password must be at least 6 characters.'); return; }
    if (newPassword !== confirmPassword) { setPwdError('Passwords do not match.'); return; }
    changeAdminPassword(newPassword);
    setCurPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPwdSuccess(true);
    toast.success('Password Changed', 'Your admin password has been updated.');
    setTimeout(() => setPwdSuccess(false), 3000);
  };

  const handleResetData = () => {
    if (resetConfirmText !== 'RESET KTEC') { return; }
    resetBusinessData();
    resetAdminData();
    setResetDone(true);
    setShowResetWarning(false);
    setResetConfirmText('');
    toast.success('Data Reset', 'Business data has been cleared.');
  };

  const users = [
    { id: 'USR-001', name: 'Admin', role: 'Administrator', email: 'admin@k-tec.lk', status: 'Active', lastLogin: '2026-07-10T16:00:00Z', permissions: ['All Access'] },
    { id: 'USR-002', name: 'Tharaka Bandara', role: 'Manager', email: 'tharaka@k-tec.lk', status: 'Active', lastLogin: '2026-07-10T09:00:00Z', permissions: ['Sales', 'Projects', 'Reports', 'Staff'] },
    { id: 'USR-003', name: 'Amara Jayawardena', role: 'Staff', email: 'amara@k-tec.lk', status: 'Active', lastLogin: '2026-07-10T08:30:00Z', permissions: ['Quotation', 'Invoice', 'CRM'] },
  ];

  const inputCls = "w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/20";

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-semibold text-gray-900 tracking-tight">Settings</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">Manage your enterprise configuration</p>
        </div>
        <button onClick={handleSave} className="h-10 px-5 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800 flex items-center gap-2">
          <Save className="w-4 h-4" />{saved ? '✓ Saved!' : 'Save Changes'}
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Settings Nav */}
        <div className="lg:w-56 flex-shrink-0">
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-2">
            {settingsSections.map(section => {
              const Icon = section.icon;
              return (
                <button key={section.id} onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all ${activeSection === section.id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                  <Icon className="w-4 h-4" />{section.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Settings Content */}
        <div className="flex-1 min-w-0">

          {/* ── BUSINESS PROFILE ─────────────────────────── */}
          {activeSection === 'business' && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-[16px] font-semibold text-gray-900">Business Profile</h3>
                <button onClick={handleSaveBusiness}
                  className="h-9 px-4 rounded-xl bg-[#7FA86A] text-white text-[12px] font-medium hover:bg-[#658C53] flex items-center gap-1.5">
                  <Save className="w-3.5 h-3.5" />
                  {bizSaved ? '✓ Saved!' : 'Save Profile'}
                </button>
              </div>

              {/* Logo Upload */}
              <div>
                <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Business Logo</label>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-200 overflow-hidden flex items-center justify-center bg-gray-50">
                    {bizLogo ? (
                      <img src={bizLogo} alt="Logo" className="w-full h-full object-contain" />
                    ) : (
                      <Building2 className="w-8 h-8 text-gray-300" />
                    )}
                  </div>
                  <div className="space-y-2">
                    <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                    <button onClick={() => logoInputRef.current?.click()}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-xl text-[12px] font-medium hover:bg-gray-200">
                      <Upload className="w-3.5 h-3.5" /> Upload Logo
                    </button>
                    {bizLogo && (
                      <button onClick={() => setBizLogo(null)}
                        className="flex items-center gap-2 px-4 py-2 text-red-500 rounded-xl text-[12px] font-medium hover:bg-red-50">
                        <X className="w-3.5 h-3.5" /> Remove
                      </button>
                    )}
                    <p className="text-[11px] text-gray-400">PNG, JPG, SVG · Max 2MB</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Business Name</label>
                  <input value={bizName} onChange={e => setBizName(e.target.value)} className={inputCls} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Tagline</label>
                  <input value={bizTagline} onChange={e => setBizTagline(e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                    <input value={bizPhone} onChange={e => setBizPhone(e.target.value)} className={`${inputCls} pl-9`} placeholder="076 282 9800" />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">WhatsApp Number</label>
                  <div className="relative">
                    <Smartphone className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                    <input value={bizWhatsapp} onChange={e => setBizWhatsapp(e.target.value)} className={`${inputCls} pl-9`} placeholder="94702161323 (no + or spaces)" />
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">Format: country code + number (e.g. 94702161323)</p>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                    <input value={bizEmail} onChange={e => setBizEmail(e.target.value)} className={`${inputCls} pl-9`} />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Website</label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                    <input value={bizWebsite} onChange={e => setBizWebsite(e.target.value)} className={`${inputCls} pl-9`} />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Address</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                    <input value={bizAddress} onChange={e => setBizAddress(e.target.value)} className={`${inputCls} pl-9`} />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Google Maps Link</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-2.5 w-4 h-4 text-[#7FA86A]" />
                    <input value={bizMapsLink} onChange={e => setBizMapsLink(e.target.value)} className={`${inputCls} pl-9`}
                      placeholder="https://www.google.com/maps/embed?pb=..." />
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">Paste the full Google Maps embed URL. This shows on the Contact page.</p>
                </div>
              </div>

              <div className="p-3 rounded-xl bg-[#F1F6EE] border border-[#D8E7D1] text-[12px] text-[#4A6741]">
                ✓ Changes are applied site-wide: public website, invoices, quotations, and admin branding.
              </div>
            </motion.div>
          )}

          {/* ── COMPANY INFO ──────────────────────────────── */}
          {activeSection === 'company' && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-6 space-y-6">
              <h3 className="text-[16px] font-semibold text-gray-900">Company Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Email</label>
                  <input value={companyEmail} onChange={e => setCompanyEmail(e.target.value)} className={inputCls} /></div>
                <div><label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">VAT Number</label>
                  <input value={vatNumber} onChange={e => setVatNumber(e.target.value)} className={inputCls} /></div>
                <div><label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Tax Rate (%)</label>
                  <input type="number" value={taxRate} onChange={e => setTaxRate(Number(e.target.value))} className={inputCls} /></div>
              </div>
            </motion.div>
          )}

          {/* ── USERS ───────────────────────────────────── */}
          {activeSection === 'users' && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-[16px] font-semibold text-gray-900">Users & Roles</h3>
                <button className="h-9 px-4 rounded-xl bg-gray-900 text-white text-[12px] font-medium hover:bg-gray-800 flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" />Add User</button>
              </div>
              <div className="space-y-3">
                {users.map(user => (
                  <div key={user.id} className="flex items-center gap-4 p-4 rounded-xl bg-gray-50/80 border border-gray-100">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center text-white text-[13px] font-semibold">{user.name.charAt(0)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-medium text-gray-900">{user.name}</div>
                      <div className="text-[12px] text-gray-400">{user.email}</div>
                    </div>
                    <div className="hidden sm:flex flex-wrap gap-1 max-w-[200px]">
                      {user.permissions.map(p => (
                        <span key={p} className="px-2 py-0.5 rounded-full bg-blue-50 text-[10px] font-medium text-blue-600">{p}</span>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded-full bg-blue-50 text-[10px] font-medium text-blue-600">{user.role}</span>
                      <button className="p-1.5 rounded-lg hover:bg-gray-200"><Edit3 className="w-3.5 h-3.5 text-gray-400" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* ── SECURITY ────────────────────────────────── */}
          {activeSection === 'security' && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-6 space-y-6">
              <h3 className="text-[16px] font-semibold text-gray-900">Security Settings</h3>
              <div className="space-y-4">

                {/* Change Admin Password */}
                <div className="p-5 rounded-xl bg-gray-50/80 border border-gray-200/60">
                  <div className="flex items-center gap-2 mb-4">
                    <Lock className="w-4 h-4 text-gray-600" />
                    <div className="text-[13px] font-semibold text-gray-900">Change Admin Password</div>
                  </div>
                  <div className="space-y-3">
                    <div className="relative">
                      <input
                        type={showCurPwd ? 'text' : 'password'}
                        placeholder="Current password"
                        value={curPassword}
                        onChange={e => { setCurPassword(e.target.value); setPwdError(''); }}
                        className="w-full h-10 px-3 pr-10 rounded-xl bg-white border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/20"
                      />
                      <button type="button" onClick={() => setShowCurPwd(v => !v)} className="absolute right-3 top-2.5 text-gray-400">
                        {showCurPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        type={showNewPwd ? 'text' : 'password'}
                        placeholder="New password (min 6 chars)"
                        value={newPassword}
                        onChange={e => { setNewPassword(e.target.value); setPwdError(''); }}
                        className="w-full h-10 px-3 pr-10 rounded-xl bg-white border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/20"
                      />
                      <button type="button" onClick={() => setShowNewPwd(v => !v)} className="absolute right-3 top-2.5 text-gray-400">
                        {showNewPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        type={showConfPwd ? 'text' : 'password'}
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={e => { setConfirmPassword(e.target.value); setPwdError(''); }}
                        className="w-full h-10 px-3 pr-10 rounded-xl bg-white border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/20"
                      />
                      <button type="button" onClick={() => setShowConfPwd(v => !v)} className="absolute right-3 top-2.5 text-gray-400">
                        {showConfPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {pwdError && (
                      <div className="flex items-center gap-2 text-[12px] text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                        <AlertTriangle className="w-3.5 h-3.5" /> {pwdError}
                      </div>
                    )}
                    {pwdSuccess && (
                      <div className="flex items-center gap-2 text-[12px] text-emerald-600 bg-emerald-50 px-3 py-2 rounded-lg">
                        <CheckCircle className="w-3.5 h-3.5" /> Password changed successfully!
                      </div>
                    )}
                    <button onClick={handleChangePassword}
                      className="h-10 px-5 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800 flex items-center gap-2">
                      <Key className="w-4 h-4" /> Update Password
                    </button>
                  </div>
                </div>

                {/* Session Timeout */}
                <div className="p-4 rounded-xl bg-gray-50/80">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[13px] font-medium text-gray-900">Session Timeout</div>
                      <div className="text-[11px] text-gray-400">Auto-logout after inactivity</div>
                    </div>
                    <select value={sessionTimeout} onChange={e => setSessionTimeout(Number(e.target.value))}
                      className="h-9 px-3 rounded-xl bg-white border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5">
                      <option value={15}>15 min</option>
                      <option value={30}>30 min</option>
                      <option value={60}>60 min</option>
                      <option value={120}>2 hours</option>
                    </select>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* ── INTEGRATIONS ────────────────────────────── */}
          {activeSection === 'integrations' && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-6 space-y-6">
              <h3 className="text-[16px] font-semibold text-gray-900">Integrations</h3>
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-gray-50/80 border border-gray-200/60">
                  <div className="flex items-center gap-3 mb-3">
                    <MessageCircle className="w-5 h-5 text-emerald-500" />
                    <div><div className="text-[13px] font-medium text-gray-900">WhatsApp Business API</div><div className="text-[11px] text-gray-400">Send invoices and quotations via WhatsApp</div></div>
                  </div>
                  <input value={whatsappApi} onChange={e => setWhatsappApi(e.target.value)} placeholder="API Key"
                    className={inputCls} />
                </div>
                <div className="p-4 rounded-xl bg-gray-50/80 border border-gray-200/60">
                  <div className="flex items-center gap-3 mb-3">
                    <Mail className="w-5 h-5 text-blue-500" />
                    <div><div className="text-[13px] font-medium text-gray-900">Email SMTP</div><div className="text-[11px] text-gray-400">Send email notifications</div></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <input placeholder="SMTP Host" className={inputCls} />
                    <input placeholder="Port" className={inputCls} />
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* ── FINANCE ─────────────────────────────────── */}
          {activeSection === 'finance' && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-6 space-y-6">
              <h3 className="text-[16px] font-semibold text-gray-900">Bank Accounts</h3>
              <div className="space-y-3">
                {[
                  { bank: 'Commercial Bank', account: '8001234567', branch: 'Kandy' },
                  { bank: 'Sampath Bank', account: '7012345678', branch: 'Colombo' },
                ].map(bank => (
                  <div key={bank.account} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50/80 border border-gray-200/60">
                    <Landmark className="w-5 h-5 text-gray-400" />
                    <div className="flex-1"><div className="text-[13px] font-medium text-gray-900">{bank.bank}</div><div className="text-[11px] text-gray-400">A/C: {bank.account} · {bank.branch}</div></div>
                    <button className="p-1.5 rounded-lg hover:bg-gray-200"><Edit3 className="w-3.5 h-3.5 text-gray-400" /></button>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* ── AI ASSISTANT ────────────────────────────── */}
          {activeSection === 'ai' && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="space-y-4">
              <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-6">
                <h3 className="text-[16px] font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Bot className="w-5 h-5 text-[#7FA86A]" /> AI Assistant Status
                </h3>
                <ToggleSwitch value={aiSettings.publicChatEnabled} onChange={v => updateAISettings({ publicChatEnabled: v })}
                  label="Public Website AI Chat" description="Show AI chat widget on the public website" />
                <ToggleSwitch value={aiSettings.voiceInputEnabled} onChange={v => updateAISettings({ voiceInputEnabled: v })}
                  label="Voice Input" description="Allow customers to speak their queries" />
                <ToggleSwitch value={aiSettings.voiceOutputEnabled} onChange={v => updateAISettings({ voiceOutputEnabled: v })}
                  label="Voice Response (TTS)" description="Read AI responses aloud when speaker button clicked" />
                <ToggleSwitch value={aiSettings.sinhalaSupportEnabled} onChange={v => updateAISettings({ sinhalaSupportEnabled: v })}
                  label="Sinhala Language Support" description="AI understands and responds in Sinhala" />
                <ToggleSwitch value={aiSettings.singlishSupportEnabled} onChange={v => updateAISettings({ singlishSupportEnabled: v })}
                  label="Singlish Support" description="AI understands romanized Sinhala (Singlish)" />
                <ToggleSwitch value={aiSettings.productRecommendationsEnabled} onChange={v => updateAISettings({ productRecommendationsEnabled: v })}
                  label="AI Product Recommendations" description="AI recommends products from catalog" />
                <ToggleSwitch value={aiSettings.packageRecommendationsEnabled} onChange={v => updateAISettings({ packageRecommendationsEnabled: v })}
                  label="AI Package Recommendations" description="AI recommends CCTV packages" />
                <ToggleSwitch value={aiSettings.quotationRequestsEnabled} onChange={v => updateAISettings({ quotationRequestsEnabled: v })}
                  label="AI Quotation Requests" description="Allow AI to create quotation requests from chat" />
              </div>
              <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-6 space-y-4">
                <h3 className="text-[16px] font-semibold text-gray-900">AI Welcome Message</h3>
                <textarea value={aiSettings.welcomeMessage} onChange={e => updateAISettings({ welcomeMessage: e.target.value })}
                  rows={4} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/20 resize-none" />
              </div>
              <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-6 space-y-4">
                <h3 className="text-[16px] font-semibold text-gray-900 flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-blue-500" /> AI Knowledge Base
                </h3>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Company Description</label>
                  <textarea value={aiSettings.companyDescription} onChange={e => updateAISettings({ companyDescription: e.target.value })}
                    rows={2} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/20 resize-none" />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Service Areas</label>
                  <input value={aiSettings.serviceAreas} onChange={e => updateAISettings({ serviceAreas: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Warranty Policy</label>
                  <input value={aiSettings.warrantyPolicy} onChange={e => updateAISettings({ warrantyPolicy: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Payment Terms</label>
                  <input value={aiSettings.paymentTerms} onChange={e => updateAISettings({ paymentTerms: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Business Instructions for AI</label>
                  <textarea value={aiSettings.businessInstructions} onChange={e => updateAISettings({ businessInstructions: e.target.value })}
                    rows={3} placeholder="Custom instructions for AI to follow when chatting with customers..."
                    className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/20 resize-none" />
                </div>
              </div>
            </motion.div>
          )}

          {/* ── ACTIVITY LOGS ────────────────────────────── */}
          {activeSection === 'activity' && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-6">
              <h3 className="text-[16px] font-semibold text-gray-900 mb-4">Activity Logs</h3>
              <div className="space-y-1">
                {activityLogs.slice(0, 20).map((log) => (
                  <div key={log.id} className="flex items-start gap-3 py-3 border-b border-gray-50 last:border-0">
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                      log.status === 'success' ? 'bg-emerald-400' :
                      log.status === 'warning' ? 'bg-amber-400' :
                      log.status === 'error' ? 'bg-red-400' : 'bg-blue-400'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-medium text-gray-900">{log.action}</span>
                        <span className="text-[11px] text-gray-400">by {log.user}</span>
                      </div>
                      <p className="text-[12px] text-gray-500 mt-0.5">{log.details}</p>
                    </div>
                    <span className="text-[11px] text-gray-400 whitespace-nowrap">{getRelativeTime(log.timestamp)}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* ── BACKUP & RESET ───────────────────────────── */}
          {activeSection === 'backup' && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="space-y-6">
              {/* Backup */}
              <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-6 space-y-4">
                <h3 className="text-[16px] font-semibold text-gray-900">Backup & Restore</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button className="p-6 rounded-2xl border-2 border-dashed border-gray-200 hover:border-gray-400 transition-all text-center group">
                    <Database className="w-8 h-8 text-gray-400 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                    <div className="text-[14px] font-medium text-gray-700">Backup Data</div>
                    <p className="text-[12px] text-gray-400 mt-1">Export all data as JSON</p>
                  </button>
                  <button className="p-6 rounded-2xl border-2 border-dashed border-gray-200 hover:border-gray-400 transition-all text-center group">
                    <Database className="w-8 h-8 text-gray-400 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                    <div className="text-[14px] font-medium text-gray-700">Restore Data</div>
                    <p className="text-[12px] text-gray-400 mt-1">Import data from backup</p>
                  </button>
                </div>
              </div>

              {/* DANGER ZONE — Reset Business Data */}
              <div className="bg-white/80 backdrop-blur-xl rounded-2xl border-2 border-red-200 p-6 space-y-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  <h3 className="text-[16px] font-semibold text-red-700">Danger Zone</h3>
                </div>
                <div className="p-4 rounded-xl bg-red-50 border border-red-200">
                  <div className="text-[14px] font-semibold text-red-800 mb-1">Reset Business Data</div>
                  <p className="text-[12px] text-red-600 mb-3">
                    This will permanently delete: <strong>Customers, Orders, Quotations, Invoices, Payments, Finance records, Warranty records, Projects.</strong>
                  </p>
                  <p className="text-[12px] text-red-600 mb-4">
                    Will NOT delete: Product Catalog, Categories, Brands, Business Settings, Admin Password.
                  </p>

                  {!showResetWarning ? (
                    <button
                      onClick={() => setShowResetWarning(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-xl text-[12px] font-medium hover:bg-red-200 border border-red-300"
                    >
                      <RefreshCw className="w-3.5 h-3.5" /> Reset Business Data
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <div className="text-[12px] font-semibold text-red-700">
                        Type <span className="font-mono bg-red-100 px-1.5 py-0.5 rounded">RESET KTEC</span> to confirm:
                      </div>
                      <input
                        value={resetConfirmText}
                        onChange={e => setResetConfirmText(e.target.value)}
                        placeholder="RESET KTEC"
                        className="w-full h-10 px-3 rounded-xl bg-white border border-red-300 text-[13px] focus:outline-none focus:ring-2 focus:ring-red-300/40 font-mono"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleResetData}
                          disabled={resetConfirmText !== 'RESET KTEC'}
                          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[12px] font-medium border transition-all ${
                            resetConfirmText === 'RESET KTEC'
                              ? 'bg-red-600 text-white hover:bg-red-700 border-red-600'
                              : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                          }`}
                        >
                          <RefreshCw className="w-3.5 h-3.5" /> Confirm Reset
                        </button>
                        <button
                          onClick={() => { setShowResetWarning(false); setResetConfirmText(''); }}
                          className="px-4 py-2 rounded-xl text-[12px] font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {resetDone && (
                    <div className="mt-3 flex items-center gap-2 text-[12px] text-emerald-600 bg-emerald-50 px-3 py-2 rounded-lg">
                      <CheckCircle className="w-3.5 h-3.5" /> Business data has been reset successfully.
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

        </div>
      </div>
    </div>
  );
}
